/* Hamakija — small UI helpers (no dependencies). Loaded with defer. */
(() => {
  const ready = (fn) => (document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn) : fn());
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
     <hk-carousel id="x"> <div class="hk-carousel__track">…slides…</div> </hk-carousel>
     Controls can live anywhere: [data-hk-prev="x"], [data-hk-next="x"], .hk-progress[data-hk-for="x"].
     Without data-hk-for they are looked up inside the closest .hk-section.
     ------------------------------------------------------------------ */
  class HkCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.hk-carousel__track');
      if (!this.track) return;
      const scope = this.closest('.hk-section') || document;
      const pick = (attr) => {
        if (this.id) {
          const own = document.querySelectorAll(`[${attr}="${CSS.escape(this.id)}"]`);
          if (own.length) return [...own];
        }
        return [...scope.querySelectorAll(`[${attr}]`)].filter((el) => !el.getAttribute(attr));
      };
      this.prev = pick('data-hk-prev');
      this.next = pick('data-hk-next');
      this.bars = this.id
        ? [...document.querySelectorAll(`.hk-progress[data-hk-for="${this.id}"] .hk-progress__bar`)]
        : [];
      if (!this.bars.length) this.bars = [...scope.querySelectorAll('.hk-progress:not([data-hk-for]) .hk-progress__bar')];

      this.onPrev = () => this.go(-1);
      this.onNext = () => this.go(1);
      this.prev.forEach((b) => b.addEventListener('click', this.onPrev));
      this.next.forEach((b) => b.addEventListener('click', this.onNext));
      this.onScroll = () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = null;
          this.update();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.ro = new ResizeObserver(() => this.update());
      this.ro.observe(this.track);
      this.initDrag();
      this.update();
    }

    disconnectedCallback() {
      this.prev?.forEach((b) => b.removeEventListener('click', this.onPrev));
      this.next?.forEach((b) => b.removeEventListener('click', this.onNext));
      this.track?.removeEventListener('scroll', this.onScroll);
      this.ro?.disconnect();
    }

    step() {
      const first = this.track.children[0];
      if (!first) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const w = first.getBoundingClientRect().width + gap;
      const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / w));
      return w * (perView > 2 ? perView - 1 : 1);
    }

    go(dir) {
      this.track.scrollBy({ left: dir * this.step(), behavior: reduceMotion() ? 'auto' : 'smooth' });
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = Math.max(0, scrollWidth - clientWidth);
      const atStart = scrollLeft <= 2;
      const atEnd = scrollLeft >= max - 2;
      this.prev.forEach((b) => (b.disabled = atStart));
      this.next.forEach((b) => (b.disabled = atEnd));
      this.toggleAttribute('data-static', max <= 2);
      const ratio = scrollWidth ? Math.min(1, clientWidth / scrollWidth) : 1;
      this.bars.forEach((bar) => {
        bar.style.width = `${ratio * 100}%`;
        bar.style.transform = `translateX(${clientWidth ? (scrollLeft / clientWidth) * 100 : 0}%)`;
      });
    }

    /* mouse drag on desktop (touch uses native scrolling) */
    initDrag() {
      let down = false, startX = 0, startLeft = 0, moved = false;
      this.track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true; moved = false; startX = e.clientX; startLeft = this.track.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 6) {
          moved = true;
          this.track.style.scrollSnapType = 'none';
          this.track.style.scrollBehavior = 'auto';
          this.track.classList.add('is-dragging');
        }
        if (moved) this.track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        if (!moved) return;
        this.track.classList.remove('is-dragging');
        const left = this.track.scrollLeft;
        this.track.style.scrollSnapType = '';
        this.track.style.scrollBehavior = '';
        this.track.scrollLeft = left;
        const stop = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
        this.track.addEventListener('click', stop, { capture: true, once: true });
        setTimeout(() => this.track.removeEventListener('click', stop, { capture: true }), 50);
      });
      this.track.addEventListener('dragstart', (e) => e.preventDefault());
    }
  }
  if (!customElements.get('hk-carousel')) customElements.define('hk-carousel', HkCarousel);

  /* ------------------------------------------------------------------
     <hk-video data-id="YOUTUBE_ID" | data-url="any YouTube link"> poster + .hk-play + [data-hk-video-frame] </hk-video>
     Shopify-hosted file: put <template data-hk-video-src>{{ video | video_tag }}</template> inside instead.
     Every video plays INLINE in its own block (no popups, never leaves the site).
     data-hk-empty = no video configured → the poster is static (no click action).
     Starting one video pauses any other video on the page.
     ------------------------------------------------------------------ */
  const ytId = (v) => {
    if (!v) return '';
    if (/^[\w-]{11}$/.test(v)) return v;
    const m = v.match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/|\/live\/)([\w-]{11})/);
    return m ? m[1] : '';
  };
  const ytSrc = (id) => `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1&modestbranding=1&enablejsapi=1&origin=${encodeURIComponent(location.origin)}`;
  const ytCommand = (frame, func) => {
    try { frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func, args: [] }), '*'); } catch (_) { /* ignore */ }
  };
  const iframe = (id, title) => {
    const f = document.createElement('iframe');
    f.src = ytSrc(id);
    f.title = title || 'YouTube';
    f.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    f.allowFullscreen = true;
    // ask the player to report its state, so we know when it starts playing
    f.addEventListener('load', () => {
      try { f.contentWindow.postMessage(JSON.stringify({ event: 'listening', id: f.dataset.hkId || '' }), '*'); } catch (_) { /* ignore */ }
    });
    return f;
  };

  class HkVideo extends HTMLElement {
    connectedCallback() {
      if (this.hkInit) return;
      this.hkInit = true;
      if (!this.source()) {
        this.setAttribute('data-hk-empty', '');
        return;
      }
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
      this.setAttribute('role', 'button');
      this.addEventListener('click', (e) => this.play(e));
      this.addEventListener('keydown', (e) => {
        if (e.target !== this) return; // keys inside the player (space = pause) belong to the player
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.play(e); }
      });
    }
    source() {
      const tpl = this.querySelector('template[data-hk-video-src]');
      if (tpl) return { type: 'file', tpl };
      if (/^\d+$/.test(this.dataset.vimeo || '')) return { type: 'vimeo', id: this.dataset.vimeo };
      const id = ytId(this.dataset.id || this.dataset.url);
      return id ? { type: 'youtube', id } : null;
    }
    play(e) {
      if (this.classList.contains('is-playing')) return; // the player itself handles clicks now
      const src = this.source();
      if (!src) return;
      e?.preventDefault();
      HkVideo.pauseOthers(this);
      const holder = this.querySelector('[data-hk-video-frame]') || this;
      if (src.type === 'file') {
        holder.appendChild(src.tpl.content.cloneNode(true));
        const v = holder.querySelector('video');
        if (v) {
          v.controls = true;
          v.playsInline = true;
          v.setAttribute('playsinline', '');
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        }
      } else if (src.type === 'vimeo') {
        const f = document.createElement('iframe');
        f.src = `https://player.vimeo.com/video/${src.id}?autoplay=1&playsinline=1&dnt=1`;
        f.title = this.getAttribute('aria-label') || 'Vimeo';
        f.allow = 'autoplay; fullscreen; picture-in-picture';
        f.allowFullscreen = true;
        holder.appendChild(f);
      } else {
        holder.appendChild(iframe(src.id, this.getAttribute('aria-label') || ''));
      }
      this.classList.add('is-playing');
      this.removeAttribute('role');
      this.removeAttribute('tabindex');
      if (HkVideo.io) HkVideo.io.observe(this);
    }
    pause() {
      const v = this.querySelector('video');
      if (v && !v.paused) v.pause();
      const f = this.querySelector('iframe');
      if (f && /vimeo\.com/.test(f.src)) { try { f.contentWindow.postMessage('{"method":"pause"}', '*'); } catch (_) { /* ignore */ } }
      else if (f) ytCommand(f, 'pauseVideo');
    }
    static pauseOthers(current) {
      document.querySelectorAll('hk-video.is-playing').forEach((o) => { if (o !== current && o.pause) o.pause(); });
    }
  }
  // pause a playing video when it is scrolled out of view (page scroll or carousel swipe)
  if ('IntersectionObserver' in window) {
    HkVideo.io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (!en.isIntersecting || en.intersectionRatio < 0.25) en.target.pause(); });
    }, { threshold: [0, 0.25] });
  }
  // a hosted <video> started from its own controls → pause the others
  document.addEventListener('play', (e) => {
    const host = e.target && e.target.closest && e.target.closest('hk-video');
    if (host) HkVideo.pauseOthers(host);
  }, true);
  // a YouTube player reports "playing" → pause the others
  window.addEventListener('message', (e) => {
    if (!/^https:\/\/www\.youtube(-nocookie)?\.com$/.test(e.origin)) return;
    let d;
    try { d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data; } catch (_) { return; }
    const state = d && d.info && typeof d.info === 'object' ? d.info.playerState : (d && d.event === 'onStateChange' ? d.info : undefined);
    if (state !== 1) return;
    const frame = [...document.querySelectorAll('hk-video iframe')].find((f) => f.contentWindow === e.source);
    if (frame) HkVideo.pauseOthers(frame.closest('hk-video'));
  });
  if (!customElements.get('hk-video')) customElements.define('hk-video', HkVideo);

  /* ------------------------------------------------------------------
     <hk-filter> chips [data-hk-filter="value|all"] → items [data-hk-tags="a,b"]
     ------------------------------------------------------------------ */
  class HkFilter extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-hk-filter]');
        if (!btn || !this.contains(btn)) return;
        e.preventDefault();
        this.apply(btn.dataset.hkFilter);
        this.querySelectorAll('[data-hk-filter]').forEach((b) => {
          const on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      });
    }
    apply(value) {
      const v = (value || 'all').toLowerCase();
      let shown = 0;
      this.querySelectorAll('[data-hk-tags]').forEach((item) => {
        const tags = item.dataset.hkTags.toLowerCase().split(',').map((t) => t.trim());
        const show = v === 'all' || tags.includes(v);
        item.hidden = !show;
        if (show) shown += 1;
      });
      const empty = this.querySelector('[data-hk-filter-empty]');
      if (empty) empty.hidden = shown > 0;
      this.querySelector('hk-carousel')?.update?.();
    }
  }
  if (!customElements.get('hk-filter')) customElements.define('hk-filter', HkFilter);

  /* ------------------------------------------------------------------
     <hk-tabs> buttons [data-hk-tab="id"] → panels [data-hk-panel="id"]
     ------------------------------------------------------------------ */
  class HkTabs extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-hk-tab]');
        if (!btn || !this.contains(btn)) return;
        e.preventDefault();
        const id = btn.dataset.hkTab;
        this.querySelectorAll('[data-hk-tab]').forEach((b) => {
          const on = b.dataset.hkTab === id;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        this.querySelectorAll('[data-hk-panel]').forEach((p) => {
          p.hidden = id !== 'all' && p.dataset.hkPanel !== id;
        });
      });
    }
  }
  if (!customElements.get('hk-tabs')) customElements.define('hk-tabs', HkTabs);

  /* ------------------------------------------------------------------
     <hk-toc data-source="#ArticleBody"> builds a list of h2 links + active state
     ------------------------------------------------------------------ */
  const slug = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ł/g, 'l')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);

  class HkToc extends HTMLElement {
    connectedCallback() {
      const src = document.querySelector(this.dataset.source || '[data-hk-toc-source]');
      const list = this.querySelector('[data-hk-toc-list]') || this;
      if (!src) return;
      const heads = [...src.querySelectorAll('h2')];
      if (!heads.length) { this.hidden = true; return; }
      const used = new Set();
      list.innerHTML = '';
      heads.forEach((h) => {
        if (!h.id) {
          let id = slug(h.textContent) || 'sekcja';
          while (used.has(id) || document.getElementById(id)) id += '-1';
          h.id = id;
        }
        used.add(h.id);
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = `#${h.id}`;
        a.textContent = h.textContent.trim();
        li.appendChild(a);
        list.appendChild(li);
      });
      const links = [...list.querySelectorAll('a')];
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          links.forEach((a) => a.classList.toggle('is-active', a.getAttribute('href') === `#${en.target.id}`));
        });
      }, { rootMargin: '0px 0px -70% 0px' });
      heads.forEach((h) => io.observe(h));
    }
  }
  if (!customElements.get('hk-toc')) customElements.define('hk-toc', HkToc);

  /* ------------------------------------------------------------------
     Delegated helpers
     ------------------------------------------------------------------ */
  // Copy link  <button data-hk-copy="url" data-hk-copied="Skopiowano">
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-hk-copy]');
    if (!btn) return;
    e.preventDefault();
    const url = btn.dataset.hkCopy || window.location.href;
    const done = () => {
      const label = btn.dataset.hkCopied;
      if (!label) return;
      const old = btn.getAttribute('aria-label');
      btn.setAttribute('aria-label', label);
      btn.classList.add('is-copied');
      setTimeout(() => { btn.classList.remove('is-copied'); if (old) btn.setAttribute('aria-label', old); }, 1800);
    };
    if (navigator.clipboard) navigator.clipboard.writeText(url).then(done, done);
  });

  // Favourites (heart buttons [data-hk-fav="handle"], remembered in this browser)
  // Counter: [data-hk-fav-count]. Every change fires document event "hk:favs" {handle, on, favs}.
  const FAV_KEY = 'hk-favs';
  const readFavs = () => { try { const v = JSON.parse(localStorage.getItem(FAV_KEY) || '[]'); return Array.isArray(v) ? v : []; } catch (_) { return []; } };
  const writeFavs = (v) => { try { localStorage.setItem(FAV_KEY, JSON.stringify(v)); } catch (_) { /* ignore */ } };
  const syncFavs = (root = document, favs = readFavs()) => {
    root.querySelectorAll('[data-hk-fav]').forEach((b) => b.setAttribute('aria-pressed', favs.includes(b.dataset.hkFav) ? 'true' : 'false'));
    document.querySelectorAll('[data-hk-fav-count]').forEach((el) => {
      el.textContent = favs.length > 99 ? '99+' : String(favs.length);
      el.hidden = favs.length === 0;
    });
  };
  window.HkFavs = { read: readFavs, write: writeFavs, sync: syncFavs };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-hk-fav]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const h = btn.dataset.hkFav;
    let favs = readFavs();
    const on = !favs.includes(h);
    favs = on ? [...favs, h] : favs.filter((x) => x !== h);
    writeFavs(favs);
    document.dispatchEvent(new CustomEvent('hk:favs', { detail: { handle: h, on, favs } }));
  });
  document.addEventListener('hk:favs', (e) => syncFavs(document, (e.detail && e.detail.favs) || readFavs()));
  window.addEventListener('storage', (e) => { if (e.key === FAV_KEY) syncFavs(); });
  // cards added later (filters, "load more", wishlist page) get the right heart state
  if ('MutationObserver' in window) {
    let t = 0;
    new MutationObserver(() => { clearTimeout(t); t = setTimeout(() => syncFavs(), 60); })
      .observe(document.documentElement, { childList: true, subtree: true });
  }

  // Exclusive accordions: <div data-hk-acc-group> <details class="hk-acc">…
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.open) return;
    const group = d.closest('[data-hk-acc-group]');
    if (!group) return;
    group.querySelectorAll('details[open]').forEach((o) => { if (o !== d) o.open = false; });
  }, true);

  // Scroll reveal
  const initReveal = (root = document) => {
    const els = root.querySelectorAll('.hk-reveal:not(.is-visible)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window) || reduceMotion() || window.Shopify?.designMode) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach((el) => io.observe(el));
  };

  ready(() => {
    syncFavs();
    initReveal();
  });
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));
})();